import React, { memo, useState, useRef } from "react";
import styled from "styled-components";
import ChatComponent from "../components/Startups/StartupChat";
import useStartupStore from "../stores/startupStore";
import ManageRequestToJoinTeam from "../components/Startups/ManageRequestToJoinTeam";
import Meeting from "../components/Startups/Meeting";
import { InviteMembers } from "../components/Startups/InviteMember";
import { useTranslation } from "react-i18next";
import FeatureButton from "./FeatureButton";
import { ActionIcon, Group, UnstyledButton} from "@mantine/core";
import { IconMessage, IconX } from "@tabler/icons-react";
import Mixpanel from "../components/Lib/mixpanel";

const MemoChatComponent = memo(ChatComponent);

// Dashboard Card Container
const DashboardCard = styled.div`
  height: 100%;
  border-radius: 8px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

// Compact Members List
const MembersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(32px, auto));
  gap: 6px;
  align-items: center;
  justify-content: start;
`;

// Smaller Avatar Styles
const MemberAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f0f0f0;
`;

const InitialAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #333;
  font-size: 12px;
`;

// Compact Dropdown Menu with Close Button
const DropdownMenu = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  padding: 12px 16px;
  margin: 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 200px;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const DropdownTitle = styled.span`
  font-weight: 500;
  font-size: 14px;
`;

const DropdownContent = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Create a context to manage modal state
const ModalContext = React.createContext();

const ModalProvider = ({ children }) => {
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  
  const openModal = () => setIsAnyModalOpen(true);
  const closeModal = () => setIsAnyModalOpen(false);
  
  return (
    <ModalContext.Provider value={{ isAnyModalOpen, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};

// Custom hook to access modal context
const useModalContext = () => React.useContext(ModalContext);

const StartupMembers = () => {
  const { t } = useTranslation();
  const { isOwner, isMember, members, slug } = useStartupStore((state) => ({
    slug: state.slug,
    isOwner: state.isOwner,
    isMember: state.isMember,
    members: state.members,
  }));

  return (
    <ModalProvider>
      <StartupMembersContent 
        isOwner={isOwner}
        isMember={isMember}
        members={members}
        slug={slug}
        t={t}
      />
    </ModalProvider>
  );
};

const StartupMembersContent = ({ isOwner, isMember, members, slug, t }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);
  const closeDropdown = () => setIsDropdownOpen(false);


  // Wrapper component to intercept clicks on modal triggers
  const ModalTrigger = ({ children, onClick }) => {
    const { openModal } = useModalContext();
    
    const handleClick = (e) => {
      openModal();
      if (onClick) onClick(e);
      // Don't close the dropdown automatically
    };
    
    return React.cloneElement(children, { onClick: handleClick });
  };

  return (
    <DashboardCard>
      <MembersList>
        {members.map((member, index) => {
          const initials = `${member.first_name ? member.first_name[0] : ""}${
            member.last_name ? member.last_name[0] : ""
          }`;
          return (
            <a key={member.id} href={`/profile/user/${member.id}`}>
              {member.avatar_url ? (
                <MemberAvatar
                  src={member.avatar_url}
                  alt={`Member ${index + 1}`}
                />
              ) : (
                <InitialAvatar>{initials}</InitialAvatar>
              )}
            </a>
          );
        })}
        {(isMember || isOwner) && members.length < 10 && (
          <ModalTrigger>
            <InviteMembers />
          </ModalTrigger>
        )}
      </MembersList>

      <div style={{ position: "relative", marginTop: "6px" }} ref={dropdownRef}>
        <FeatureButton buttonText={t("startups_page.team_actions")} action={toggleDropdown} />
        {isDropdownOpen && (
          <DropdownMenu>
            <DropdownHeader>
              <ActionIcon size="sm" variant="subtle" onClick={closeDropdown}>
                <IconX size={16} />
              </ActionIcon>
            </DropdownHeader>
            
            <DropdownContent>
              {!(isMember || isOwner) ? (
                <>
                  <li>
                    <ModalTrigger>
                      <InviteMembers />
                    </ModalTrigger>
                  </li>
                  <li>
                    <ModalTrigger>
                      <Meeting canSchedule={false} slug={slug} />
                    </ModalTrigger>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <UnstyledButton
                      onClick={() => {
                        setOpenChat(true);
                        closeDropdown(); // Close dropdown when chat opens
                        Mixpanel.feature('Chat', 'Opened', { source: 'Team Actions Dropdown' });
                      }}
                    >
                      <Group>
                        <ActionIcon variant="subtle">
                          <IconMessage size={24} />
                        </ActionIcon>
                        <p style={{ fontSize: "16px" }}>
                          {t("startups_page.members.open_chat")}
                        </p>
                      </Group>
                    </UnstyledButton>
                  </li>
                  <li>
                    <ModalTrigger>
                      <ManageRequestToJoinTeam />
                    </ModalTrigger>
                  </li>
                  <li>
                    <ModalTrigger>
                      <Meeting canSchedule={true} slug={slug} />
                    </ModalTrigger>
                  </li>
                </>
              )}
            </DropdownContent>
          </DropdownMenu>
        )}
      </div>

      {(isMember || isOwner) && openChat && (
        <MemoChatComponent startupId={slug} setOpenChat={setOpenChat} />
      )}
    </DashboardCard>
  );
};

export default StartupMembers;